import {
  ChakraBox,
  ChakraDivider,
  ChakraHStack,
  FAIconName,
  FireflyButton,
  InputGroupV2,
  ModalV2,
  Text,
  UniversalSelect,
} from '@equip.health/ui';
import { useState } from 'react';
import CareTeamMemberCard from '~/components/maud/patients/profile/tabs/careplan/CareTeamMemberCard';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { PatientEvents } from '~/analytics/patientEvents';
import { CareTeamTranslations } from '~/lib/constants/careTeam.constants';
import { CareTeamMemberCardProps } from '~/lib/types/careTeam/careTeam';
import { useGetProviderTeamVoidReasonsQuery } from '~/store/services/treatmentEpisodes';

const TOP_LEVEL_ID = 'void-members-care-team-modal';

interface CareTeamVoidAssignmentsModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (members: CareTeamMemberCardProps[]) => void;
  careTeamMembers: CareTeamMemberCardProps[];
  isLoading: boolean;
  patientId: string;
}

const modalArgs = {
  footerStyle: {
    padding: '0px',
  },
  titleStyle: {
    flex: '1 0 0',
    padding: '0px',
  },
};

const CareTeamVoidAssignmentsModal = ({
  careTeamMembers,
  onCancel,
  onConfirm,
  isOpen,
  isLoading,
  patientId,
}: CareTeamVoidAssignmentsModalProps) => {
  const [membersWithVoidAssignments, setMembersWithVoidAssignments] = useState(
    careTeamMembers.filter((member) => member.isVoidAssignment),
  );

  const { data: voidReasons, isLoading: isGetVoidReasonsLoading } =
    useGetProviderTeamVoidReasonsQuery();

  const isSingleMember = membersWithVoidAssignments?.length === 1;

  const handleSetVoidReason = (userId: string, voidReason: string) => {
    const updatedMembers = membersWithVoidAssignments.map((member) => {
      if (member.id === userId) {
        return {
          ...member,
          voidReason,
        };
      }
      return member;
    });

    setMembersWithVoidAssignments(updatedMembers);
  };

  const textContent = isSingleMember
    ? CareTeamTranslations.VOID_ASSIGNMENT_DISCLAIMER
    : CareTeamTranslations.VOID_ASSIGNMENT_DISCLAIMER_MULTIPLE;

  const options = voidReasons?.map((reason) => ({
    label: reason.label,
    value: reason.code,
  }));

  const allMembersHaveVoidReasons = membersWithVoidAssignments.every(
    (member) => !!member.voidReason,
  );
  const { trackEvent } = useAnalytics();

  return (
    <ModalV2
      footerContent={
        <ChakraHStack justifyContent="space-between" width="100%">
          <FireflyButton
            id={`${TOP_LEVEL_ID}__cancel`}
            leftIcon={FAIconName.timesRegular}
            onClick={() => {
              trackEvent(
                PatientEvents.providerAssignmentVoidAssignmentCancelButtonClicked,
                {
                  patientId,
                  source: AnalyticsSourceValues.CARE_PLAN_TAB,
                },
              );
              onCancel();
            }}
            variant="secondary"
          >
            {CareTeamTranslations.CANCEL}
          </FireflyButton>
          <FireflyButton
            aria-label="Confirm void assignment"
            data-testid={`${TOP_LEVEL_ID}__confirm`}
            id={`${TOP_LEVEL_ID}__confirm`}
            isDisabled={!allMembersHaveVoidReasons}
            isLoading={isLoading}
            leftIcon={FAIconName.trashRegular}
            onClick={() => {
              trackEvent(
                PatientEvents.providerAssignmentVoidAssignmentConfirmButtonClicked,
                {
                  patientId,
                  source: AnalyticsSourceValues.CARE_PLAN_TAB,
                },
              );
              onConfirm(membersWithVoidAssignments);
            }}
            variant="accent.critical"
          >
            {isSingleMember
              ? CareTeamTranslations.VOID_ASSIGNMENT
              : CareTeamTranslations.VOID_ASSIGNMENTS}
          </FireflyButton>
        </ChakraHStack>
      }
      {...modalArgs}
      data-testid={`${TOP_LEVEL_ID}__modal`}
      id={`${TOP_LEVEL_ID}__modal`}
      isOpen={isOpen}
      onClose={() => {
        trackEvent(
          PatientEvents.providerAssignmentVoidAssignmentModalCloseButtonClicked,
          {
            patientId,
            source: AnalyticsSourceValues.CARE_PLAN_TAB,
          },
        );
        onCancel();
      }}
      title={
        isSingleMember
          ? CareTeamTranslations.VOID_ASSIGNMENT
          : CareTeamTranslations.VOID_ASSIGNMENTS
      }
    >
      <Text>{textContent}</Text>

      <ChakraBox maxHeight="660px" paddingTop="24px" width="100%">
        {membersWithVoidAssignments?.map((member, idx) => (
          <ChakraBox
            key={member.id}
            paddingBottom={
              idx === membersWithVoidAssignments.length - 1 ? '24px' : undefined
            }
            width="100%"
          >
            <CareTeamMemberCard
              id={`${TOP_LEVEL_ID}__care-team-member-card__${member.id}`}
              key={member.id}
              {...member}
              isVoidAssignment
            />
            <ChakraBox paddingTop="24px" width="100%">
              <InputGroupV2
                id={`${TOP_LEVEL_ID}_template__group`}
                isRequired
                label={CareTeamTranslations.VOID_REASON}
              >
                <ChakraBox paddingTop="6px">
                  <UniversalSelect
                    id={`${TOP_LEVEL_ID}_template__select`}
                    isClearable={false}
                    isLoading={isGetVoidReasonsLoading}
                    isSingleSelect
                    modalId={`${TOP_LEVEL_ID}__modal-modal`}
                    onChange={(reason) => {
                      trackEvent(
                        PatientEvents.providerAssignmentVoidReasonSelected,
                        {
                          patientId,
                          source: AnalyticsSourceValues.CARE_PLAN_TAB,
                        },
                      );
                      handleSetVoidReason(member.id, reason.value);
                    }}
                    options={options}
                    placeholder={CareTeamTranslations.VOID_REASON_PLACEHOLDER}
                  />
                </ChakraBox>
              </InputGroupV2>
            </ChakraBox>

            {!isSingleMember && idx < membersWithVoidAssignments.length - 1 && (
              <ChakraDivider
                colorScheme="line"
                data-testid={`${TOP_LEVEL_ID}__divider`}
                id={`${TOP_LEVEL_ID}__divider`}
                marginBottom="24px"
                marginTop="18px"
              />
            )}
          </ChakraBox>
        ))}
      </ChakraBox>
    </ModalV2>
  );
};

export default CareTeamVoidAssignmentsModal;
